// ConceptFilter.js
// Ce module gère les composants de recherche et de filtrage

import React, { useState } from 'react';
import { Search, X, Grid, List, Filter, PieChart, Info, ChevronDown, ChevronUp, Tag, AlertTriangle  } from 'lucide-react';

// Composant pour les filtres et la visualisation
const ConceptFilters = ({ 
  searchQuery, 
  setSearchQuery, 
  groupBy, 
  setGroupBy, 
  viewMode, 
  setViewMode,
  hasContextFilter,
  setHasContextFilter,
  categoriesFilter,
  setCategoriesFilter,
  availableCategories
}) => {
  const [showFilters, setShowFilters] = useState(false);
  
  return (
    <div className="space-y-3">
      {/* Barre de recherche principale et contrôles */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder="Rechercher un concept..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Bascule vue grille/liste */}
          <div className="border border-gray-300 rounded-lg p-1 flex items-center">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
              title="Vue grille"
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
              title="Vue liste"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          
          {/* Affichage/masquage des filtres */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 ${showFilters ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-white'}`}
          >
            <Filter className="h-4 w-4" />
            <span className="text-sm font-medium">Filtres</span>
            {showFilters ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          
          {/* Tri */}
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700"
          >
            <option value="relevance">Par pertinence</option>
            <option value="alphabetical">Par ordre alphabétique</option>
            <option value="count">Par nombre de documents</option>
            <option value="category">Par catégorie</option>
          </select>
        </div>
      </div>
      
      {/* Filtres avancés (affichés conditionnellement) */}
      {showFilters && (
        <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Filtre par disponibilité de résumé */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Tag className="h-3.5 w-3.5 text-emerald-600" />
                <span>Résumé</span>
              </h3>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasContextFilter === null}
                    onChange={() => setHasContextFilter(null)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">Tous les concepts</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasContextFilter === true}
                    onChange={() => setHasContextFilter(true)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">Avec résumé uniquement</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasContextFilter === false}
                    onChange={() => setHasContextFilter(false)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">Sans résumé uniquement</span>
                </label>
              </div>
            </div>
            
            {/* Filtre par catégories (si disponibles) */}
            {availableCategories.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                  <PieChart className="h-3.5 w-3.5 text-indigo-600" />
                  <span>Catégories</span>
                </h3>
                <div className="space-y-2 max-h-32 overflow-y-auto pr-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={categoriesFilter.length === 0}
                      onChange={() => setCategoriesFilter([])}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">Toutes les catégories</span>
                  </label>
                  {availableCategories.map(category => (
                    <label key={category} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={categoriesFilter.includes(category)}
                        onChange={() => {
                          if (categoriesFilter.includes(category)) {
                            setCategoriesFilter(categoriesFilter.filter(c => c !== category));
                          } else {
                            setCategoriesFilter([...categoriesFilter, category]);
                          }
                        }}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">{category}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {/* Suggestions / Aide */}
            <div className="sm:ml-auto bg-blue-50 border border-blue-100 rounded-lg p-3 max-w-xs">
              <div className="flex gap-2">
                <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-700 mb-1">Conseils de navigation</h4>
                  <ul className="text-xs text-blue-600 space-y-1 list-disc pl-4">
                    <li>Utilisez les filtres pour affiner vos résultats</li>
                    <li>Les concepts avec résumé sont enrichis d'informations spécifiques à vos documents</li>
                    <li>La vue en liste affiche plus de détails sur chaque concept</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant pour afficher les résultats de filtrage
const FilterResultsSummary = ({ 
  filteredCount, 
  hasContextFilter, 
  categoriesFilter, 
  searchQuery,
  resetFilters 
}) => {
  return (
    <div className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm border border-gray-100">
      <p className="text-sm text-gray-600">
        {filteredCount} concept{filteredCount > 1 ? 's' : ''} trouvé{filteredCount > 1 ? 's' : ''}
        {hasContextFilter === true && (
          <span className="ml-1 text-emerald-700">avec résumé</span>
        )}
        {hasContextFilter === false && (
          <span className="ml-1 text-gray-500">sans résumé</span>
        )}
        {categoriesFilter.length > 0 && (
          <span className="ml-1 text-indigo-700">
            dans {categoriesFilter.length === 1 ? 'la catégorie' : 'les catégories'} : {categoriesFilter.join(', ')}
          </span>
        )}
      </p>
      
      {(searchQuery || hasContextFilter !== null || categoriesFilter.length > 0) && (
        <button
          onClick={resetFilters}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <X className="h-3 w-3" />
          Réinitialiser
        </button>
      )}
    </div>
  );
};

// Composant qui affiche un message quand aucun résultat n'est trouvé
const NoResultsFound = ({ 
  hasFilters, 
  resetFilters 
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
      <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
      <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun concept trouvé</h3>
      <p className="text-gray-600">
        {hasFilters ? 
          'Aucun concept ne correspond à vos critères de recherche.' : 
          "Aucun concept n'a été identifié dans vos documents"
        }
      </p>
      {hasFilters && (
        <button
          onClick={resetFilters}
          className="mt-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
        >
          Réinitialiser les filtres
        </button>
      )}
    </div>
  );
};

export { ConceptFilters, FilterResultsSummary, NoResultsFound };